import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ShieldCheck,
  Search,
  Filter,
  Eye,
  EyeOff,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Monitor,
  Camera,
  ChevronDown,
  RefreshCw,
  X,
  Shield,
  User,
  Activity,
  Maximize2,
  Image,
  Scan,
  Ban,
  ArrowLeft,
  Loader2,
  Info,
  ScanFace,
  MicOff,
  Volume2,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

// ─── Student-backend base URL for snapshot images ─────────────────────────────
// Snapshots are stored on the student dashboard backend (port 5000)
const STUDENT_API = process.env.REACT_APP_STUDENT_API_URL && !process.env.REACT_APP_STUDENT_API_URL.includes('5001') ? process.env.REACT_APP_STUDENT_API_URL : `http://${window.location.hostname}:5000`;

// ─── Violation Meta (labels, colors, icons) ───────────────────────────────────
const VIOLATION_META = {
  // Browser / window
  tab_switch:           { label: 'Tab Switch',           color: 'amber',   icon: Monitor        },
  minimize:             { label: 'Window Minimized',     color: 'amber',   icon: ChevronDown    },
  fullscreen_exit:      { label: 'Fullscreen Exited',    color: 'orange',  icon: Maximize2      },
  inactivity:           { label: 'Inactivity',           color: 'gray',    icon: Clock          },
  // Face detection
  face_absent:          { label: 'No Face Detected',     color: 'yellow',  icon: Camera         },
  multiple_faces:       { label: 'Multiple Faces',       color: 'red',     icon: User           },
  face_mismatch:        { label: 'Face Mismatch',        color: 'red',     icon: ScanFace       },
  face_covered:         { label: 'Face Covered',         color: 'amber',   icon: Scan           },
  // Liveness / attention
  attention_check_fail: { label: 'Attention Check Fail', color: 'orange',  icon: Activity       },
  identity_verified:    { label: 'Identity Verified',    color: 'green',   icon: CheckCircle    },
  face_registered:      { label: 'Face Registered',      color: 'blue',    icon: Scan           },
  // Eye gaze (NEW)
  gaze_away:            { label: 'Gaze Away',            color: 'amber',   icon: Eye            },
  eyes_closed:          { label: 'Eyes Closed',          color: 'yellow',  icon: EyeOff         },
  // Audio / voice (NEW)
  voice_detected:       { label: 'Voice Detected',       color: 'purple',  icon: Volume2        },
  prolonged_silence:    { label: 'Prolonged Silence',    color: 'gray',    icon: MicOff         },
};

const SEVERITY_COLORS = {
  info:     'border-l-blue-400   bg-blue-50   dark:bg-blue-900/20  text-blue-700   dark:text-blue-300',
  low:      'border-l-amber-400  bg-amber-50  dark:bg-amber-900/20 text-amber-700  dark:text-amber-300',
  medium:   'border-l-orange-400 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300',
  high:     'border-l-red-400    bg-red-50    dark:bg-red-900/20   text-red-700    dark:text-red-300',
  critical: 'border-l-rose-600   bg-rose-50   dark:bg-rose-900/20  text-rose-700   dark:text-rose-300',
};

const STATUS_CONFIG = {
  live:       { label: 'Live',       textColor: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-800/40', dot: 'bg-emerald-500', pulse: true },
  flagged:    { label: 'Flagged',    textColor: 'text-amber-700 dark:text-amber-400',     bg: 'bg-amber-50 dark:bg-amber-900/20',     border: 'border-amber-200 dark:border-amber-800/40',   dot: 'bg-amber-500',  pulse: false },
  completed:  { label: 'Completed',  textColor: 'text-blue-700 dark:text-blue-400',       bg: 'bg-blue-50 dark:bg-blue-900/20',       border: 'border-blue-200 dark:border-blue-800/40',     dot: 'bg-blue-500',   pulse: false },
  terminated: { label: 'Terminated', textColor: 'text-red-700 dark:text-red-400',         bg: 'bg-red-50 dark:bg-red-900/20',         border: 'border-red-200 dark:border-red-800/40',       dot: 'bg-red-500',    pulse: false },
  active:     { label: 'Live',       textColor: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-800/40', dot: 'bg-emerald-500', pulse: true },
};

// ─── Risk Score Bar Component ─────────────────────────────────────────────────
const RiskBar = ({ score }) => {
  const pct = Math.min(100, Math.max(0, score || 0));
  const color = pct >= 75 ? 'bg-red-500' : pct >= 60 ? 'bg-orange-500' : pct >= 40 ? 'bg-amber-400' : 'bg-emerald-500';
  const label = pct >= 75 ? 'Critical' : pct >= 60 ? 'High' : pct >= 40 ? 'Medium' : 'Low';
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-[10px] font-black uppercase tracking-wide flex-shrink-0 ${
        pct >= 60 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'
      }`}>{label}</span>
    </div>
  );
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value }) => (
  <div className="group relative bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/35 p-4 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] overflow-hidden">
    <div className="relative flex items-center justify-between">
      <div>
        <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{value}</p>
      </div>
      <div className="w-11 h-11 rounded-xl bg-[#00152e] dark:bg-[#1a3884] flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
        <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
      </div>
    </div>
  </div>
);

// ─── Session Detail Panel ─────────────────────────────────────────────────────
const SessionDetailPanel = ({ sessionId, onClose, onTerminate }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [confirmTerminate, setConfirmTerminate] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/proctoring/sessions/${sessionId}`);
        if (res.data.success) setData(res.data.data);
      } catch (err) {
        toast.error('Failed to load session details');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [sessionId, onClose]);

  const handleTerminate = async () => {
    setTerminating(true);
    try {
      await api.post(`/proctoring/sessions/${sessionId}/terminate`);
      toast.success('Session terminated and student locked out');
      onTerminate(sessionId);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to terminate session');
    } finally {
      setTerminating(false);
      setConfirmTerminate(false);
    }
  };

  const session = data?.session;
  const events  = data?.events || [];
  const result  = data?.result;

  const snapshots = events.filter(e => e.screenshotUrl);
  const violations = events.filter(e => e.severity !== 'info');
  const isLive = session?.status === 'live' || session?.status === 'active';

  const formatTime = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getSnapshotUrl = (url) => {
    if (!url) return null;
    
    // If it's an absolute URL, ensure it points to the correct 5000 port,
    // in case it was saved incorrectly in the database as 5001.
    if (url.startsWith('http')) {
      try {
        const urlObj = new URL(url);
        if (urlObj.port === '5001') {
          urlObj.port = '5000';
          return urlObj.toString();
        }
        return url;
      } catch (e) {
        return url;
      }
    }
    
    return `${STUDENT_API}${url.startsWith('/') ? url : `/${url}`}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />

      {/* Sliding panel */}
      <div className="relative z-10 w-full max-w-3xl h-full bg-white dark:bg-[#002147] border-l border-gray-200 dark:border-[#1a3884]/40 shadow-2xl flex flex-col overflow-hidden animate-slide-in-right">

        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-r from-[#00152E] to-[#1a3884] text-white">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h2 className="text-lg font-extrabold tracking-tight">Session Review</h2>
                <p className="text-xs text-white/70">{loading ? 'Loading...' : session?.studentName}</p>
              </div>
            </div>
            {!loading && isLive && (
              <div className="flex items-center gap-2 bg-white/10 border border-white/20 px-3 py-1.5 rounded-xl">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs font-bold text-emerald-300">LIVE</span>
              </div>
            )}
          </div>

          {!loading && session && (
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/10 rounded-xl p-3">
                <p className="text-[10px] text-white/50 uppercase tracking-wider mb-0.5">Risk Score</p>
                <p className={`text-xl font-black ${(session.riskScore || 0) >= 60 ? 'text-red-300' : 'text-emerald-300'}`}>
                  {session.riskScore || 0}<span className="text-sm font-normal text-white/50">/100</span>
                </p>
              </div>
              <div className="bg-white/10 rounded-xl p-3">
                <p className="text-[10px] text-white/50 uppercase tracking-wider mb-0.5">Violations</p>
                <p className="text-xl font-black text-amber-300">{session.totalViolations || 0}</p>
              </div>
              <div className="bg-white/10 rounded-xl p-3">
                <p className="text-[10px] text-white/50 uppercase tracking-wider mb-0.5">Duration</p>
                <p className="text-xl font-black text-white">{session.duration || '-'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 flex border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-[#002147]">
          {['overview', 'timeline', 'snapshots'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider transition-all border-b-2 ${
                activeTab === tab
                  ? 'border-[#1a3884] text-[#1a3884] dark:text-indigo-400 dark:border-indigo-400'
                  : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
            >
              {tab === 'overview' ? 'Overview' : tab === 'timeline' ? `Timeline (${violations.length})` : `Snapshots (${snapshots.length})`}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50/50 dark:bg-[#001530]/50">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <Loader2 className="w-8 h-8 text-[#1a3884] animate-spin" />
              <p className="text-sm text-gray-400">Loading session data...</p>
            </div>
          ) : !session ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <Info className="w-10 h-10 text-gray-300" />
              <p className="text-sm text-gray-400">Session data unavailable</p>
            </div>
          ) : (
            <>
              {/* OVERVIEW TAB */}
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  {/* Student Info */}
                  <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center text-white font-bold text-lg shadow-sm">
                        {session.studentName?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-extrabold text-gray-900 dark:text-white">{session.studentName}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{session.rollNumber} · {session.email}</p>
                      </div>
                      <div className="ml-auto">
                        {(() => {
                          const cfg = STATUS_CONFIG[session.status] || STATUS_CONFIG.completed;
                          return (
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${cfg.textColor} ${cfg.bg} ${cfg.border}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} ${cfg.pulse ? 'animate-pulse' : ''}`} />
                              {cfg.label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <InfoItem label="Assessment" value={session.assessmentTitle} />
                      <InfoItem label="Code" value={session.assessmentCode} />
                      <InfoItem label="Started" value={`${formatDate(session.startedAt)} ${formatTime(session.startedAt)}`} />
                      <InfoItem label="Duration" value={session.duration} />
                    </div>
                  </div>

                  {/* Risk Summary */}
                  <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm">
                    <h3 className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                      <Shield className="w-3.5 h-3.5" /> Risk Assessment
                    </h3>
                    <div className="mb-3">
                      <div className="flex justify-between mb-1">
                        <span className="text-xs text-gray-500">Risk Score</span>
                        <span className={`text-xs font-black ${(session.riskScore || 0) >= 60 ? 'text-red-600' : 'text-emerald-600'}`}>{session.riskScore || 0}/100</span>
                      </div>
                      <div className="w-full h-2.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        {(() => {
                          const pct = Math.min(100, session.riskScore || 0);
                          const color = pct >= 75 ? 'bg-red-500' : pct >= 60 ? 'bg-orange-500' : pct >= 40 ? 'bg-amber-400' : 'bg-emerald-500';
                          return <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />;
                        })()}
                      </div>
                    </div>
                    {session.violationsByType && Object.keys(session.violationsByType).length > 0 && (
                      <div className="space-y-1.5 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                        {Object.entries(session.violationsByType).map(([type, count]) => {
                          const meta = VIOLATION_META[type] || { label: type, color: 'gray' };
                          return (
                            <div key={type} className="flex items-center justify-between text-xs">
                              <span className="text-gray-600 dark:text-gray-400">{meta.label}</span>
                              <span className="font-bold px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{count}x</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Identity & Environment */}
                  <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm">
                    <h3 className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                      <ScanFace className="w-3.5 h-3.5" /> Identity & Environment
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <CheckItem label="Face Registered" value={session.faceRegistered} />
                      <CheckItem label="Identity Verified" value={session.identityVerified} />
                      <CheckItem label="Camera Granted" value={session.camera} />
                      <CheckItem label="Fullscreen" value={session.fullscreen} />
                    </div>
                    {session.faceVerificationPassRate != null && (
                      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                        <div className="flex justify-between mb-1">
                          <span className="text-xs text-gray-500">Face Verification Pass Rate</span>
                          <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                            {Math.round((session.faceVerificationPassRate || 0) * 100)}%
                          </span>
                        </div>
                        <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 rounded-full"
                            style={{ width: `${Math.round((session.faceVerificationPassRate || 0) * 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                      <InfoItem label="Browser" value={session.browserInfo || '-'} />
                      <InfoItem label="Screen" value={session.screenResolution || '-'} />
                    </div>
                  </div>

                  {/* Result info if completed/locked */}
                  {result && (
                    <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm">
                      <h3 className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                        <Activity className="w-3.5 h-3.5" /> Assessment Result
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        <InfoItem label="Status" value={result.completionStatus || '-'} />
                        <InfoItem label="Questions Answered" value={`${result.answeredQuestions || 0} / ${result.totalQuestions || 0}`} />
                        <InfoItem label="Submitted" value={result.submittedAt ? formatTime(result.submittedAt) : 'Not submitted'} />
                        {result.lockedOut && <InfoItem label="Lockout Reason" value={result.lockoutReason || '-'} highlight="red" />}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TIMELINE TAB */}
              {activeTab === 'timeline' && (
                <div className="space-y-3">
                  {events.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
                      <CheckCircle className="w-10 h-10 opacity-30" />
                      <p className="text-sm font-medium">No events recorded</p>
                    </div>
                  ) : (
                    events.map((event, idx) => {
                      const meta = VIOLATION_META[event.eventType] || { label: event.eventType, color: 'gray' };
                      const EventIcon = meta.icon || Info;
                      const sevClass = SEVERITY_COLORS[event.severity] || SEVERITY_COLORS.low;
                      return (
                        <div key={event._id || idx} className={`relative flex gap-3 border-l-4 rounded-r-xl p-3 ${sevClass}`}>
                          <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-white dark:bg-white/10 flex items-center justify-center shadow-sm mt-0.5">
                            <EventIcon className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <span className="text-xs font-bold">{meta.label}</span>
                              <span className="text-[10px] font-mono opacity-60 flex-shrink-0">{formatTime(event.timestamp)}</span>
                            </div>
                            {event.details && (
                              <p className="text-[11px] mt-0.5 opacity-80 leading-relaxed">{event.details}</p>
                            )}
                            {event.screenshotUrl && (
                              <button
                                onClick={() => setLightboxImg(getSnapshotUrl(event.screenshotUrl))}
                                className="inline-flex items-center gap-1 text-[10px] mt-1.5 font-semibold underline underline-offset-2 opacity-70 hover:opacity-100 transition-opacity"
                              >
                                <Image className="w-3 h-3" /> View snapshot
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* SNAPSHOTS TAB */}
              {activeTab === 'snapshots' && (
                <div>
                  {snapshots.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
                      <Camera className="w-10 h-10 opacity-30" />
                      <p className="text-sm font-medium">No snapshots captured</p>
                      <p className="text-xs opacity-60">Webcam snapshots are captured when violations occur</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {snapshots.map((event, idx) => {
                        const meta = VIOLATION_META[event.eventType] || { label: event.eventType };
                        const imgUrl = getSnapshotUrl(event.screenshotUrl);
                        return (
                          <button
                            key={event._id || idx}
                            onClick={() => setLightboxImg(imgUrl)}
                            className="group relative rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 text-left h-40"
                          >
                            <img
                              src={imgUrl}
                              alt={meta.label}
                              className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                              onError={e => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            {/* Fallback container when image fails */}
                            <div className="hidden w-full h-full flex-col items-center justify-center bg-gray-50 dark:bg-[#00152e] text-gray-400 p-2 text-center">
                              <Camera className="w-8 h-8 mb-1 opacity-50" />
                              <span className="text-[10px] font-medium opacity-70">Image unavailable</span>
                              <span className="text-[7px] mt-1 opacity-40 break-all leading-tight max-w-full overflow-hidden">{imgUrl || 'No URL saved'}</span>
                            </div>
                            
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent px-3 py-2.5 pt-8">
                              <p className="text-[11px] font-bold text-white truncate">{meta.label}</p>
                              <p className="text-[9px] text-gray-300 font-medium">{formatTime(event.timestamp)}</p>
                            </div>
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 backdrop-blur-md rounded-lg p-1.5 shadow-sm">
                              <Eye className="w-3.5 h-3.5 text-white" />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Admin Actions Footer */}
        {!loading && session && (
          <div className="flex-shrink-0 px-6 py-4 bg-white dark:bg-[#002147] border-t border-gray-100 dark:border-gray-800">
            {confirmTerminate ? (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-red-800 dark:text-red-300">Confirm Termination</p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                      This will immediately lock out the student, auto-submit their exam, and cannot be undone.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirmTerminate(false)}
                    className="flex-1 py-2 text-xs font-bold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleTerminate}
                    disabled={terminating}
                    className="flex-1 py-2 text-xs font-bold bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {terminating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                    {terminating ? 'Terminating...' : 'Yes, Terminate Exam'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 py-2.5 text-sm font-semibold bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl transition-colors"
                >
                  Close
                </button>
                {(isLive || session.status === 'flagged') && (
                  <button
                    onClick={() => setConfirmTerminate(true)}
                    className="flex-1 py-2.5 text-sm font-bold bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors flex items-center justify-center gap-2 shadow-md shadow-red-200 dark:shadow-red-900/30"
                  >
                    <Ban className="w-4 h-4" />
                    Terminate Exam
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90"
          onClick={() => setLightboxImg(null)}
        >
          <button className="absolute top-4 right-4 p-2 bg-white/10 rounded-xl text-white hover:bg-white/20 transition-colors">
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxImg}
            alt="Violation snapshot"
            className="max-w-[90vw] max-h-[85vh] rounded-xl object-contain shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

// ─── Small helper sub-components ─────────────────────────────────────────────
const InfoItem = ({ label, value, highlight }) => (
  <div>
    <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-0.5">{label}</p>
    <p className={`text-xs font-semibold truncate ${highlight === 'red' ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
      {value || '-'}
    </p>
  </div>
);

const CheckItem = ({ label, value }) => (
  <div className="flex items-center gap-2">
    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${value ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' : 'bg-red-100 dark:bg-red-900/30 text-red-500'}`}>
      {value ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    </div>
    <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PROCTOR PAGE
// ─────────────────────────────────────────────────────────────────────────────
const Proctor = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [lastRefresh, setLastRefresh] = useState('—');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const autoRefreshRef = useRef(null);

  const fetchSessions = useCallback(async (showSpinner = false) => {
    if (showSpinner) setIsRefreshing(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (search.trim()) params.search = search.trim();
      const res = await api.get('/proctoring/sessions', { params });
      if (res.data.success) {
        setSessions(res.data.data || []);
        setLastRefresh(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      }
    } catch (err) {
      if (showSpinner) {
        toast.error('Failed to fetch sessions');
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [filterStatus, search]);

  // Initial load
  useEffect(() => {
    setLoading(true);
    fetchSessions();
    // eslint-disable-next-line
  }, [filterStatus]);

  // Auto-refresh interval
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => fetchSessions(), 15000);
    } else {
      clearInterval(autoRefreshRef.current);
    }
    return () => clearInterval(autoRefreshRef.current);
  }, [autoRefresh, fetchSessions]);

  // Counts from data
  const counts = {
    live: sessions.filter(s => s.status === 'live' || s.status === 'active').length,
    flagged: sessions.filter(s => s.status === 'flagged').length,
    completed: sessions.filter(s => s.status === 'completed').length,
    terminated: sessions.filter(s => s.status === 'terminated').length,
  };

  const handleTerminated = (sessionId) => {
    setSessions(prev => prev.map(s =>
      s._id === sessionId ? { ...s, status: 'terminated' } : s
    ));
  };

  // Client-side search filter
  const filtered = sessions.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (s.studentName || '').toLowerCase().includes(q) ||
      (s.rollNumber || '').toLowerCase().includes(q) ||
      (s.assessmentTitle || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50/50 dark:bg-[#00152E]/5 p-4 lg:p-6 space-y-6">

      {/* Header */}
      <div className="glass dark:bg-[#002147]/80 rounded-xl shadow-md border border-white/30 dark:border-[#1a3884]/30 p-4 mb-6 animate-fade-in">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="p-2.5 bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-blue-400 hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884] dark:hover:border-[#1a3884] dark:hover:text-white rounded-xl transition-all border border-gray-200 dark:border-[#1a3884]/45 group shadow-sm flex items-center justify-center shrink-0"
              title="Back"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#00152E] dark:text-white mb-1 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[#1a3884]" />
                Proctor Dashboard
              </h1>
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <span className="font-medium">Real-time exam monitoring &amp; integrity management</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto justify-start sm:justify-end flex-wrap">
            {/* Last refresh info */}
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-[#002147] px-3 py-2.5 rounded-xl border border-gray-200 dark:border-[#1a3884]/50 shadow-sm font-medium">
              <Clock className="w-3.5 h-3.5" />
              Updated: {lastRefresh}
            </div>

            {/* Manual refresh (Outline button) */}
            <button
              onClick={() => fetchSessions(true)}
              disabled={isRefreshing}
              className="flex items-center gap-2 text-sm px-4 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-[#00152e] border border-gray-200 dark:border-[#1a3884]/50 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 rounded-xl transition-colors font-medium shadow-sm disabled:opacity-70"
            >
              <RefreshCw className={`w-4 h-4 text-gray-500 dark:text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            {/* Auto-refresh toggle */}
            <button
              onClick={() => setAutoRefresh(v => !v)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-all shadow-sm ${
                autoRefresh
                  ? 'bg-[#1a3884] text-white border border-[#00152E]/20 hover:bg-[#00152E]'
                  : 'bg-white dark:bg-[#002147] border border-gray-200 dark:border-[#1a3884]/50 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20'
              }`}
              title="Auto-refresh every 15 seconds"
            >
              <Activity className={`w-4 h-4 ${autoRefresh ? 'animate-pulse text-white' : 'text-gray-500 dark:text-gray-400'}`} />
              Auto Refresh {autoRefresh ? 'On' : 'Off'}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Activity}
          label="Live Sessions"
          value={loading ? '—' : counts.live}
        />
        <StatCard
          icon={AlertTriangle}
          label="Flagged"
          value={loading ? '—' : counts.flagged}
        />
        <StatCard
          icon={CheckCircle}
          label="Completed"
          value={loading ? '—' : counts.completed}
        />
        <StatCard
          icon={XCircle}
          label="Terminated"
          value={loading ? '—' : counts.terminated}
        />
      </div>

      {/* Table Section */}
      <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">

        {/* Table Controls */}
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-gray-100 dark:border-gray-800">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              id="proctor-search"
              type="text"
              placeholder="Search by student, roll, or assessment..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 dark:bg-[#001f40] border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1a3884]/50"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <select
              id="proctor-status-filter"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="text-sm bg-gray-50 dark:bg-[#001f40] border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#1a3884]/50 cursor-pointer"
            >
              <option value="all">All Sessions</option>
              <option value="live">Live</option>
              <option value="flagged">Flagged</option>
              <option value="completed">Completed</option>
              <option value="terminated">Terminated</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-800">
                <th className="text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-5 py-3">Student</th>
                <th className="text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-3">Assessment</th>
                <th className="text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-3">Status</th>
                <th className="text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-3">Duration</th>
                <th className="text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-3">Risk</th>
                <th className="text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-3">Flags</th>
                <th className="text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-3">Checks</th>
                <th className="text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-16">
                    <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-[#1a3884]" />
                    <p className="text-sm text-gray-400">Loading sessions...</p>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-gray-400 dark:text-gray-500">
                    <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">No sessions found</p>
                    <p className="text-xs mt-1">
                      {sessions.length === 0
                        ? 'No proctoring sessions have been recorded yet'
                        : 'Try adjusting your search or filter'}
                    </p>
                  </td>
                </tr>
              ) : (
                filtered.map(session => {
                  const cfg = STATUS_CONFIG[session.status] || STATUS_CONFIG.completed;
                  const isLive = session.status === 'live' || session.status === 'active';
                  const isFlagged = session.status === 'flagged';
                  const riskScore = session.riskScore || 0;

                  return (
                    <tr
                      key={session._id}
                      className="hover:bg-gray-50/70 dark:hover:bg-[#001f40]/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedSessionId(session._id)}
                    >
                      {/* Student */}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center text-white font-bold text-sm shadow-sm flex-shrink-0">
                            {(session.studentName || 'U').charAt(0)}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">{session.studentName || 'Unknown'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{session.rollNumber || '-'}</p>
                          </div>
                        </div>
                      </td>

                      {/* Assessment */}
                      <td className="px-4 py-4">
                        <p className="font-medium text-gray-800 dark:text-gray-200 max-w-[180px] truncate" title={session.assessmentTitle}>
                          {session.assessmentTitle || '-'}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {session.startedAt ? new Date(session.startedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}
                        </p>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${cfg.textColor} ${cfg.bg} ${cfg.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} ${cfg.pulse ? 'animate-pulse' : ''}`} />
                          {cfg.label}
                        </span>
                      </td>

                      {/* Duration */}
                      <td className="px-4 py-4">
                        <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-medium text-xs">
                          <Clock className="w-3.5 h-3.5 text-gray-400" />
                          {session.duration || '-'}
                        </span>
                      </td>

                      {/* Risk Score */}
                      <td className="px-4 py-4 min-w-[100px]">
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span className={`text-xs font-bold ${riskScore >= 60 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                              {riskScore}
                            </span>
                          </div>
                          <RiskBar score={riskScore} />
                        </div>
                      </td>

                      {/* Flags */}
                      <td className="px-4 py-4">
                        {(session.totalViolations || 0) > 0 ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-bold ${
                            (session.totalViolations || 0) >= 5
                              ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                              : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                          }`}>
                            <AlertTriangle className="w-3 h-3" />
                            {session.totalViolations} {session.totalViolations === 1 ? 'flag' : 'flags'}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 font-medium flex items-center gap-1">
                            <CheckCircle className="w-3 h-3 text-emerald-500" /> Clean
                          </span>
                        )}
                      </td>

                      {/* Integrity Checks */}
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1.5">
                          <span
                            title={session.camera ? 'Camera active' : 'Camera not granted'}
                            className={`p-1.5 rounded-lg ${session.camera ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400'}`}
                          >
                            <Camera className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-4 text-right" onClick={e => e.stopPropagation()}>
                        <button
                          id={`proctor-view-${session._id}`}
                          onClick={() => setSelectedSessionId(session._id)}
                          className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors shadow-sm flex items-center gap-1.5 ml-auto ${
                            isLive || isFlagged
                              ? 'bg-[#1a3884] hover:bg-[#00152E] text-white'
                              : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <Eye className="w-3 h-3" />
                          {isLive ? 'Monitor' : 'Review'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <p className="text-xs text-gray-400">
            Showing {filtered.length} of {sessions.length} sessions
          </p>
          <p className="text-xs text-gray-400 flex items-center gap-1.5">
            {autoRefresh ? (
              <>
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Auto-refreshing every 15s
              </>
            ) : (
              <>
                <span className="w-2 h-2 rounded-full bg-gray-300" />
                Auto-refresh off
              </>
            )}
          </p>
        </div>
      </div>

      {/* Session Detail Side Panel */}
      {selectedSessionId && (
        <SessionDetailPanel
          sessionId={selectedSessionId}
          onClose={() => setSelectedSessionId(null)}
          onTerminate={handleTerminated}
        />
      )}
    </div>
  );
};

export default Proctor;
